import React from 'react';

import styled from 'styled-components';

import { StyledTable } from 'common/Table/StyledTable';
import { AICompetitiveIntelligenceScoringRow as ScoringResultsRow } from 'types/recommendations';

import AiCiTableWrapper, {
  AiCiSection,
  AiCiSectionTitle,
  CommunityName,
  CommunitySubtitle,
  RankCell,
} from './AiCiTableWrapper';

type Criterion = {
  key?: string;
  label?: string;
};

type Props = {
  rows: ScoringResultsRow[];
  criteria: Criterion[];
  primaryCommunityName?: string | null;
  title?: string;
};

const ScoreText = styled.span`
  font-size: 14px;
  color: var(--text-primary);
`;

const OverallScoreText = styled.span`
  font-size: 14px;
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

const RANK_COLUMN_WIDTH = 60;
const COMMUNITY_COLUMN_WIDTH = 200;
const CRITERION_COLUMN_WIDTH = 160;
const OVERALL_COLUMN_WIDTH = 90;

const asNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

const ScoringResultsTable = ({ rows, criteria, primaryCommunityName, title = 'Scoring Results' }: Props) => {
  const ranked = [...rows]
    .map((row, originalIndex) => ({ row, originalIndex }))
    .sort((a, b) => {
      const aScore = asNumber(a.row.overall_score) ?? -Infinity;
      const bScore = asNumber(b.row.overall_score) ?? -Infinity;
      if (bScore !== aScore) return bScore - aScore;
      // The API supplies canonical tie order; browser locale sorting can disagree with its ranking table.
      return a.originalIndex - b.originalIndex;
    })
    .map(({ row }, i) => ({ ...row, __rank: asNumber(row.overall_score) != null ? i + 1 : null }));

  const isPrimary = (row: ScoringResultsRow) => primaryCommunityName != null && row.community === primaryCommunityName;

  const columns = [
    {
      title: 'RANK',
      dataIndex: '__rank',
      key: '__rank',
      width: RANK_COLUMN_WIDTH,
      render: (rank: number | null) => <RankCell>{rank == null ? '—' : `#${rank}`}</RankCell>,
    },
    {
      title: '',
      dataIndex: 'community',
      key: 'community',
      width: COMMUNITY_COLUMN_WIDTH,
      render: (_: unknown, row: ScoringResultsRow) => (
        <>
          <CommunityName>{row.community}</CommunityName>
          <CommunitySubtitle className={isPrimary(row) ? 'primary' : ''}>
            {isPrimary(row) ? 'My Community' : 'Competitor'}
          </CommunitySubtitle>
        </>
      ),
    },
    ...criteria.map((c) => ({
      title: (c.label ?? c.key ?? '').toUpperCase(),
      dataIndex: `${c.key}_score`,
      key: `${c.key}_score`,
      width: CRITERION_COLUMN_WIDTH,
      align: 'center' as const,
      render: (value: unknown) => <ScoreText>{asNumber(value) ?? '—'}</ScoreText>,
    })),
    {
      title: 'OVERALL',
      dataIndex: 'overall_score',
      key: 'overall_score',
      align: 'center' as const,
      fixed: 'right' as const,
      width: OVERALL_COLUMN_WIDTH,
      render: (value: unknown) => <OverallScoreText>{asNumber(value) ?? '—'}</OverallScoreText>,
    },
  ];
  const scrollWidth =
    RANK_COLUMN_WIDTH + COMMUNITY_COLUMN_WIDTH + criteria.length * CRITERION_COLUMN_WIDTH + OVERALL_COLUMN_WIDTH;

  return (
    <AiCiSection>
      <AiCiSectionTitle>{title}</AiCiSectionTitle>
      <AiCiTableWrapper className="mini-scroll">
        <StyledTable
          columns={columns as any}
          dataSource={ranked}
          rowKey={((row: ScoringResultsRow & { __rank: number | null }) => `${row.__rank}-${row.community}`) as any}
          rowClassName={((row: ScoringResultsRow) => (isPrimary(row) ? 'primary' : '')) as any}
          pagination={false}
          scroll={{ x: scrollWidth }}
          size="middle"
          bordered={false}
        />
      </AiCiTableWrapper>
    </AiCiSection>
  );
};

export default ScoringResultsTable;
